/**
 * PusherCommChannel handles real-time messaging functionality using Pusher
 */

const messageEventName = 'client-message';

/**
 * Generate a channel name for a stream
 * @param {int} streamId -- from db
 * @returns {string} Channel name
 */
function _generateChannelName(streamId) {
  if (!streamId) return null;
  return `presence-stream-${streamId}`;
}

export default class PusherCommChannel {
  constructor(client, options = {}) {
    this.client = client;
    this.streamId = options.streamId || null;
    this.uid = options.uid || null;
    this.debug = options.debug || false;
    this.onMessageReceived = options.onMessageReceived || (() => {});
    this.onMemberJoined = options.onMemberJoined || (() => {});
    this.onMemberLeft = options.onMemberLeft || (() => {});

    // Store user ID for message identification
    this.userId = options.userId || null;

    // Public channel reference
    this.channel = null;
    this.initialized = false;
  }

  /**
   * Initialize the Pusher comm channel
   * @param {Object} config - Configuration options
   */
  async initialize(config = {}) {
    if (config.uid) {
      this.uid = config.uid;
    }
    if (config.streamId) {
      this.streamId = config.streamId;
    }
    this.initialized = true;
    this._log('PusherCommChannel initialized');
  }

  /**
   * Connect to a channel - this adapts to use the existing channel subscriptions
   * @param {id} streamId - from db
   */
  async connect(streamId = null) {
    // Store the room name for message routing
    if (streamId) {
      this.streamId = streamId;
    }

    if (!this.streamId) {
      this._log('Cannot connect: No streamId available');
      return false;
    }

    try {
      // Ensure the pusher client is available
      if (!this.client) {
        this._log('Cannot connect: Pusher client not available');
        return false;
      }

      // Generate the Pusher channel name using the room name
      const newChannelName = _generateChannelName(this.streamId);

      if (this.channel) {
        if (this.channel.name === newChannelName) {
          this._log('Already connected to the channel:', newChannelName);
          return true;
        }
        // Changing channels, unsubscribe from the current one first
        this.unsubscribeFromChannel();
      }

      this.channel = this._subscribeToChannel(newChannelName);
      if (!this.channel) {
        this._log('Failed to subscribe to channel');
        return false;
      }

      // Bind channel logged events
      this.channel.bind(
        'pusher:subscription_succeeded',
        () => {
          this._log(
            'Subscription succeeded event for channel:',
            newChannelName,
          );
        },
      );
      this.channel.bind(
        'pusher:subscription_error',
        (error) => {
          this._log(
            'Subscription error event for channel:',
            newChannelName,
            'error:',
            error,
          );
        },
      );
      // Debugging, log all events on the channel
      this.channel.bind_global((eventName, data) => {
        // eslint-disable-next-line no-console
        this._log('Received event:', eventName, data);
      });

      // Bind custom message event
      this.channel.bind(
        messageEventName,
        this._handleMessageReceived,
        this,
      );

      // Bind presence channel events
      this.channel.bind(
        'pusher:member_added',
        this._handleMemberJoined,
        this,
      );
      this.channel.bind(
        'pusher:member_removed',
        this._handleMemberLeft,
        this,
      );

      this._log(
        `Subscribed and bound events for channel: ${newChannelName}`,
      );
      return true;
    } catch (error) {
      this._error('Failed to connect to Pusher:', error);
      return false;
    }
  }

  _handleMessageReceived({ message, sender_id: senderId }, metadata) {
    // Handle incoming (custom) messages

    this._log(
      'Message received:',
      message,
      'senderId:',
      senderId,
      'metadata:',
      metadata,
    );
    if (this.onMessageReceived) {
      this.onMessageReceived(
        message,
        metadata?.user_id || senderId || 'unknown',
      );
    }
  }

  _handleMemberJoined(member) {
    this._log('Member joined:', member);
    if (this.onMemberJoined) {
      this.onMemberJoined(member);
    }
  }

  _handleMemberLeft(member) {
    this._log('Member left:', member);
    if (this.onMemberLeft) {
      this.onMemberLeft(member);
    }
  }

  /**
   * Unbind and unsubscribe from the current channel
   */
  disconnect() {
    if (!this.channel) {
      this._log('No existing channel to unsubscribe from');
      return;
    }
    this.channel.unbind(messageEventName);
    this.channel.unbind('pusher:member_added');
    this.channel.unbind('pusher:member_removed');
    this._unsubscribeFromChannel();
    this.channel = null;
  }

  /**
   * Toggle channel subscription based on connection state
   * @param {boolean} connected - Whether to subscribe or unsubscribe
   * @param {Object} streamId - Stream ID -- required if not set yet.
   */
  toggleSubscription(connected, streamId = null) {
    if (streamId) {
      this.streamId = streamId;
    }

    if (!this.streamId) {
      this._log('Cannot toggle subscription: stream id unavailable');
      return;
    }

    if (connected) {
      this.connect(this.streamId);
    } else {
      // Unsubscribe from the channel
      this._unsubscribeFromChannel();
    }
  }

  /**
   * Send a message via the bound Pusher channel
   * @param {Object} message - Message to send
   */
  async sendMessage(message) {
    try {
      if (!this.channel) {
        this._error('Not bound to a Pusher channel');
        return false;
      }

      // Wrap message with additional metadata
      const wrappedMessage = {
        sender_id: this.uid || 'anonymous',
        message, // Original message
        timestamp: new Date().toISOString(),
      };

      // Trigger a client event on the channel
      const result = this.channel.trigger(
        messageEventName,
        wrappedMessage,
      );

      this._log('Message sent:', message, 'Result:', result);
      return true;
    } catch (error) {
      this._error('Failed to send message:', error);
      return false;
    }
  }

  /**
   * Get the number of members in the current channel
   * @returns {number} Member count
   */
  getMemberCount() {
    try {
      if (this.channel && this.channel.members) {
        return this.channel.members.count;
      }
    } catch (error) {
      this._log('Error getting member count:', error);
    }
    return 0;
  }

  /**
   * Check if connected to a channel
   * @returns {boolean} Connection status
   */
  isConnected() {
    return !!this.channel;
  }

  /**
   * Subscribe to a stream channel
   * @param {string} channelName - Name of the channel to subscribe to
   * @returns {Object|null} Channel object
   */
  _subscribeToChannel(channelName) {
    if (!channelName) {
      this._log('Cannot subscribe: No channel name provided');
      return null;
    }

    this._log(`Subscribing to channel: ${channelName}`);

    try {
      this.channel = this.client.subscribe(channelName);
      return this.channel;
    } catch (error) {
      // eslint-disable-next-line no-console
      this._error('Failed to subscribe to channel:', error);
      return null;
    }
  }

  _unsubscribeFromChannel() {
    if (!this.channel) {
      this._log('No channel to unsubscribe from');
      return;
    }
    const channelName = this.channel.name;
    this.client.unsubscribe(channelName);
    this._log(`Unsubscribed from channel: ${channelName}`);
  }

  /**
   * Log a debug message
   */
  _log(...args) {
    if (this.debug) {
      // eslint-disable-next-line no-console
      console.log('[PusherCommChannel]', ...args);
    }
  }

  /**
   * Log an error message
   */
  _error(...args) {
    if (this.debug) {
      // eslint-disable-next-line no-console
      console.error('[PusherCommChannel]', ...args);
    }
  }
}
